import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../component/Sidebar";
import BackButton from "../component/BackButton";
import toast from "react-hot-toast";

const LeaveHistory = () => {
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaveHistory = async () => {
    try {
      // UPDATED URL: Now points to the specific history endpoint in leave-records
      const res = await axios.get("/api/leave-records/history");
      
      const actualData = Array.isArray(res.data)
        ? res.data
        : res.data.data || res.data.leaves || [];
      setLeaves(actualData);
    } catch (error) {
      console.error("Fetch Error:", error);
      toast.error("Failed to fetch leave history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveHistory();
  }, []);

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-GB").replace(/\//g, "-")
      : "-";

  const formatDateTime = (date) =>
    date
      ? new Date(date)
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          .replace(/\//g, "-")
      : "-";

  const getByRole = (leave = {}, role) => {
    const history = Array.isArray(leave.history) ? leave.history : [];
    const direct = history.find((h) => h.role === role);
    if (direct) return direct;

    const isSameAuthority =
      leave.reportingManagerEmployeeUserId &&
      leave.departmentHeadEmployeeUserId &&
      leave.reportingManagerEmployeeUserId === leave.departmentHeadEmployeeUserId;

    if (isSameAuthority) {
      return history.find((h) => h.role === "RM & DH") || {};
    }

    return {};
  };

  return (
    <div className="flex min-h-screen flex-col md:flex-row bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-y-auto p-2 sm:p-3 md:p-4 lg:p-6">
        <div className="bg-white shadow-md rounded-md p-2 sm:p-3 md:p-4">
          <div className="bg-blue-50 border border-blue-300 rounded-lg shadow-md p-2 mb-3 sm:mb-4 flex flex-row justify-between items-center gap-2">
            <h2 className="text-sm sm:text-xl font-bold text-blue-900 whitespace-nowrap">
              Employee Leave History
            </h2>
            <div className="flex shrink-0">
              <BackButton />
            </div>
          </div>

          <div className="w-full overflow-x-auto">
            <table className="min-w-[900px] w-full border border-blue-900 text-xs sm:text-sm border-collapse">
              <thead className="bg-blue-900 text-white text-[10px] sm:text-xs md:text-sm">
                <tr>
                  <th className="border px-2 py-2">Emp ID</th>
                  <th className="border px-2 py-2">Emp Name</th>
                  <th className="border px-2 py-2">Apply Date</th>
                  <th className="border px-2 py-2">From Date</th>
                  <th className="border px-2 py-2">To Date</th>
                  <th className="border px-2 py-2">Total Days</th>
                  <th className="border px-2 py-2">RM</th>
                  <th className="border px-2 py-2">Status</th>
                  <th className="border px-2 py-2">Date & Time</th>
                  <th className="border px-2 py-2 bg-orange-600">DH</th>
                  <th className="border px-2 py-2 bg-orange-600">Status</th>
                  <th className="border px-2 py-2 bg-orange-600">Date & Time</th>
                  <th className="border px-2 py-2 bg-orange-600 text-white">Final Status</th>
                </tr>
              </thead>

              <tbody className="text-center">
                {loading ? (
                  <tr>
                    <td colSpan="13" className="py-10 text-gray-500 italic">
                      Loading leave history...
                    </td>
                  </tr>
                ) : leaves.length > 0 ? (
                  leaves.map((leave) => {
                    const rm = getByRole(leave, "Reporting Manager");
                    const dh = getByRole(leave, "Department Head");

                    return (
                      <tr key={leave._id} className="hover:bg-blue-50 transition text-xs">
                        <td className="border px-2 py-1.5 font-medium">{leave.employeeId}</td>
                        <td className="border px-2 py-1.5">{leave.employeeName}</td>
                        <td className="border px-2 py-1.5">{formatDate(leave.applicationDate)}</td>
                        <td className="border px-2 py-1.5">{formatDate(leave.fromDate)}</td>
                        <td className="border px-2 py-1.5">{formatDate(leave.toDate)}</td>
                        <td className="border px-2 py-1.5 font-semibold">{leave.noOfDays}</td>
                        <td className="border px-2 py-1.5">{leave.reportingManager || "-"}</td>
                        <td className={`border px-2 py-1.5 font-bold ${
                          rm.status === 'APPROVED' ? 'text-green-600' : rm.status === 'REJECTED' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {rm.status || "-"}
                        </td>
                        <td className="border px-2 py-1.5 text-[10px]">{formatDateTime(rm.date)}</td>
                        <td className="border px-2 py-1.5">{leave.departmentHead || "-"}</td>
                        <td className={`border px-2 py-1.5 font-bold ${
                          dh.status === 'APPROVED' ? 'text-green-600' : dh.status === 'REJECTED' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {dh.status || "-"}
                        </td>
                        <td className="border px-2 py-1.5 text-[10px]">{formatDateTime(dh.date)}</td>
                        <td className={`border px-2 py-1.5 font-bold ${
                          leave.approveRejectedStatus === 'APPROVED' ? 'text-green-700' : leave.approveRejectedStatus === 'REJECTED' ? 'text-red-700' : 'text-blue-600'
                        }`}>
                          {leave.approveRejectedStatus || "PENDING"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="13" className="py-10 text-gray-500">
                      No leave history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveHistory;